import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../hooks/use-auth';
import { useProfileQuery } from '../hooks/use-profile-query';

interface AuthGateProps {
  /** When true, redirect away from the route if the user has not finished
   *  onboarding (no profile yet). Defaults to true. */
  readonly requireProfile?: boolean;
  readonly fallback: ReactNode;
  readonly children: ReactNode;
}

export function AuthGate({ requireProfile = true, fallback, children }: AuthGateProps) {
  const { user, isInitialized } = useAuth();
  const profile = useProfileQuery({ enabled: !!user });

  if (!isInitialized) return <>{fallback}</>;

  if (!user) {
    // Not signed in — let the router-level handler decide what to render
    // (typically the landing page with an auth modal trigger).
    return <Navigate to="/" replace />;
  }

  if (!requireProfile) return <>{children}</>;

  if (profile.isLoading) return <>{fallback}</>;
  const status = (profile.error as { statusCode?: number } | undefined)?.statusCode;
  const noProfile = status === 404 || (!profile.data && !profile.isLoading);
  if (noProfile) {
    return <Navigate to="/onboarding" replace />;
  }
  return <>{children}</>;
}

export function FullPageLoader({ message }: { readonly message?: string }) {
  const { t } = useTranslation();
  return (
    <div className="nw-splash">
      <div className="nw-feed__loading-spinner" aria-hidden />
      <span>{message ?? t('common.states.loading')}</span>
    </div>
  );
}
